import { useEffect, useState, type CSSProperties } from 'react';

import { useStorefront } from '../../app/StorefrontLayout.js';
import { assetUrl } from '../../lib/publicApi.js';

interface StoreImageProps {
  /** Relative media path or absolute URL; null/undefined → the store logo shows. */
  src: string | null | undefined;
  alt: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  /** Padding (any CSS length/%) around the logo when the fallback is showing. */
  logoPad?: string;
}

/**
 * Storefront <img> that falls back to the STORE LOGO whenever the image is missing
 * or fails to load — so a product / category / brand / slide without a photo (or a
 * broken URL) shows the brand mark instead of a hollow placeholder. The logo is
 * `object-contain` with padding (inline style, to reliably beat a caller's
 * `object-cover` class). Pass sizing/rounding via `className`.
 */
export function StoreImage({ src, alt, className, loading = 'lazy', logoPad = '14%' }: StoreImageProps): JSX.Element {
  const { site } = useStorefront();
  const logo = assetUrl(site.logoUrl);
  const resolved = assetUrl(src ?? null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [resolved]);

  const showLogo = (!resolved || failed) && !!logo;
  const finalSrc = showLogo ? logo : resolved;

  if (!finalSrc) return <div className={className} aria-hidden />;

  const logoStyle: CSSProperties = { objectFit: 'contain', padding: logoPad, opacity: 0.9 };
  return (
    <img
      src={finalSrc}
      alt={alt}
      loading={loading}
      onError={() => setFailed(true)}
      className={className}
      style={showLogo ? logoStyle : undefined}
    />
  );
}
